import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from './lib/api'
import { ApiError as _ApiError } from './lib/api'
import { farmerApi } from './lib/farmer'
import { buyerApi } from './lib/buyer'
import { maskedPhone } from './lib/format'
import { Banner, IconCash, IconChevron, IconCog, IconLeaf, IconScale, IconStore } from './ui/kit'

/**
 * One front door. You say who you are, then you prove it.
 *
 * The chooser is an affordance, not a security boundary — it sends you to the right
 * form and the right home screen afterwards. What you may actually do is decided by
 * the roles on your account, on the server, every request.
 */

type Door = 'chooser' | 'field' | 'office' | 'farmer' | 'buyer' | 'admin'

const DOORS: Array<{
  key: Door
  title: string
  who: string
  detail: string
  icon: JSX.Element
}> = [
  {
    key: 'farmer',
    title: 'I grow and sell',
    who: 'Farmers',
    detail: 'Tell us what you will have, see when we are coming, and check what you were paid.',
    icon: <IconLeaf size={20} color="currentColor" />,
  },
  {
    key: 'buyer',
    title: 'I buy and sell it on',
    who: 'Market traders · shops · hotels · restaurants',
    detail: 'See today’s prices, order what you need, and check what you owe.',
    icon: <IconStore size={20} />,
  },
  {
    key: 'field',
    title: 'I collect and deliver',
    who: 'Agents · store clerks · drivers',
    detail: 'Today’s round, weighing and paying farmers, and deliveries. Works without signal.',
    icon: <IconScale size={20} />,
  },
  {
    key: 'office',
    title: 'I run the business',
    who: 'Managers · accounts · sales',
    detail: 'Today’s figures, what you bought, your buyers, orders and the money.',
    icon: <IconCash size={20} />,
  },
]

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const { door } = useParams()
  const nav = useNavigate()
  const current = (door as Door) ?? 'chooser'

  if (current === 'chooser') return <Chooser onPick={(d) => nav(`/login/${d}`)} />

  // Farmers and buyers both sign in with a phone and a code — neither has a work
  // email, and neither should have to keep a password.
  if (current === 'farmer') {
    return (
      <PhoneDoor
        onBack={() => nav('/login')}
        title="Farmer sign-in"
        heroTitle="Tell us what you have."
        heroText="Tell us what you will harvest and when. We give you a collection day, come to you, weigh it in front of you on a proper scale, and pay you the same day."
        facts={[
          ['No', 'password to remember — just your phone'],
          ['13:00', 'you are paid before this, the day we collect'],
        ]}
        hint="The number you are registered with."
        footer="If your number is not registered, ask the agent who collects from you to add it."
        requestCode={(m) => farmerApi.requestCode(m)}
        verify={async (m, c) => { await farmerApi.verify(m, c) }}
        goTo="/f"
      />
    )
  }

  if (current === 'buyer') {
    return (
      <PhoneDoor
        onBack={() => nav('/login')}
        title="Buyer sign-in"
        heroTitle="Order before dawn."
        heroText="See what we have and what it costs, order what you need for the day, and know when the van is coming. You pay for what you take, not for what was loaded."
        facts={[
          ['No', 'password to remember — just your phone'],
          ['1', 'van, at your stall, in your delivery window'],
        ]}
        hint="The number we have for you."
        footer="If your number does not work, ask us to add it to your account."
        requestCode={(m) => buyerApi.requestCode(m)}
        verify={async (m, c) => { await buyerApi.verify(m, c) }}
        goTo="/b"
      />
    )
  }

  return (
    <StaffDoor
      door={current}
      onBack={() => nav('/login')}
      onSignedIn={onSignedIn}
    />
  )
}

// ---------------------------------------------------------------- chooser
function Chooser({ onPick }: { onPick: (d: Door) => void }) {
  return (
    <div className="login-page">
      <aside className="login-art">
        <div className="row" style={{ gap: 10 }}>
          <span className="rail-mark"><IconLeaf size={16} color="#fff" /></span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Ledger</span>
        </div>

        <div>
          <h2>Every kilo accounted for.</h2>
          <p>
            Buy from farmers, weigh it three times on the way, pay them the same day, and see
            what you are making on a kilo while there is still time to do something about it.
          </p>
        </div>

        <div className="login-facts">
          <div className="login-fact"><b>3</b><span>times everything is weighed — farm, store, buyer</span></div>
          <div className="login-fact"><b>0</b><span>kilos can go missing without somebody explaining why</span></div>
          <div className="login-fact"><b>13:00</b><span>farmers are paid by this time, the day they deliver</span></div>
        </div>
      </aside>

      <div className="login-form-wrap">
        <div className="login-form" style={{ maxWidth: 420 }}>
          <div>
            <h1>Sign in</h1>
            <p className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
              Which of these are you?
            </p>
          </div>

          <div className="door-list">
            {DOORS.map((d) => (
              <button key={d.key} className="door" onClick={() => onPick(d.key)}>
                <span className="door-icon">{d.icon}</span>
                <span className="door-body">
                  <b>{d.title}</b>
                  <span className="door-who">{d.who}</span>
                  <span className="door-detail">{d.detail}</span>
                </span>
                <IconChevron size={16} />
              </button>
            ))}
          </div>

          <div className="divider" />

          <button className="admin-link" onClick={() => onPick('admin')}>
            <IconCog size={13} />
            Administrator sign-in
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- staff
const DEMO: Record<string, Array<{ email: string; who: string; role: string }>> = {
  field: [
    { email: 'agent@ledger.test', who: 'Grace Njeri', role: 'Field agent' },
    { email: 'depot@ledger.test', who: 'Brian Otieno', role: 'Depot clerk' },
    { email: 'driver@ledger.test', who: 'Sam Kiprop', role: 'Driver' },
  ],
  office: [
    { email: 'finance@ledger.test', who: 'Mary Achieng', role: 'Finance' },
    { email: 'sales@ledger.test', who: 'Faith Wambui', role: 'Sales' },
  ],
  admin: [
    { email: 'ops@ledger.test', who: 'Daniel Mwangi', role: 'Admin + operations' },
  ],
}

const TITLES: Record<string, { heading: string; sub: string }> = {
  field: { heading: 'Field team', sub: 'Use the email or phone number you were registered with.' },
  office: { heading: 'Operations', sub: 'Use your work email.' },
  admin: { heading: 'Administrator', sub: 'Full access to settings, users and reference data.' },
}

function StaffDoor({
  door, onBack, onSignedIn,
}: {
  door: Door
  onBack: () => void
  onSignedIn: () => void
}) {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const t = TITLES[door] ?? TITLES.office!

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.login(identifier.trim(), password)
      onSignedIn()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <aside className="login-art">
        <div className="row" style={{ gap: 10 }}>
          <span className="rail-mark"><IconLeaf size={16} color="#fff" /></span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Ledger</span>
        </div>
        <div>
          <h2>{t.heading}</h2>
          <p>
            {door === 'admin'
              ? 'This account can change the rules the rest of the system runs on — grade sheets, tolerances, corridor gates and who may do what. Every change it makes is recorded.'
              : door === 'field'
                ? 'Everything you need at the farmgate, and it keeps working with no signal. Payments queue on the phone and send themselves when you are back on a network.'
                : 'The four numbers that decide the business — shrink, density, fill rate and days to get paid — on every screen that can move them.'}
          </p>
        </div>
        <div />
      </aside>

      <div className="login-form-wrap">
        <form className="login-form" onSubmit={submit}>
          <button type="button" className="btn quiet" style={{ alignSelf: 'flex-start', minHeight: 30 }} onClick={onBack}>
            <IconChevron size={14} dir="left" /> Not you?
          </button>

          <div>
            <h1>{t.heading}</h1>
            <p className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>{t.sub}</p>
          </div>

          {door === 'admin' && (
            <Banner tone="warn" title="Administrator access">
              Prefer a role account for day-to-day work. Sign in here only when you need to change
              settings or users.
            </Banner>
          )}

          <div className="field">
            <label htmlFor="identifier">{door === 'field' ? 'Email or phone' : 'Email'}</label>
            <input
              id="identifier"
              className="input"
              autoComplete="username"
              inputMode={door === 'field' ? 'text' : 'email'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={door === 'field' ? '0712 345 678' : 'you@company.co.ke'}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && <div className="error-text">{error}</div>}

          <button className="btn lg block" disabled={busy || !identifier || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {DEMO[door] && (
            <>
              <div className="divider" />
              <div>
                <div className="micro" style={{ marginBottom: 8 }}>Demo accounts — password “ledger”</div>
                <div className="demo-accounts">
                  {DEMO[door]!.map((d) => (
                    <button
                      key={d.email}
                      type="button"
                      onClick={() => { setIdentifier(d.email); setPassword('ledger') }}
                    >
                      <span><b>{d.who}</b> <span className="faint">· {d.role}</span></span>
                      <span className="mono faint" style={{ fontSize: 'var(--fs-2xs)' }}>{d.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- farmer
/**
 * The phone-and-code door, used by farmers and by buyers.
 *
 * Neither has a work email, and neither should have to keep a password for something
 * they open once a day at five in the morning. One component so the two cannot drift
 * apart; the wording and where it leads are the only differences.
 */
function PhoneDoor({
  onBack, title, heroTitle, heroText, facts, hint, footer, requestCode, verify: doVerify, goTo,
}: {
  onBack: () => void
  title: string
  heroTitle: string
  heroText: string
  facts: Array<[string, string]>
  hint: string
  footer: string
  requestCode: (msisdn: string) => Promise<{ devCode?: string }>
  verify: (msisdn: string, code: string) => Promise<void>
  goTo: string
}) {
  const nav = useNavigate()
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const [msisdn, setMsisdn] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devCode, setDevCode] = useState<string | null>(null)

  async function sendCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const res = await requestCode(msisdn.trim())
      setDevCode(res.devCode ?? null)
      setStep('code')
    } catch (err) {
      setError(err instanceof _ApiError ? err.message : 'Could not send a code.')
    } finally { setBusy(false) }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await doVerify(msisdn.trim(), code.trim())
      nav(goTo, { replace: true })
    } catch (err) {
      setError(err instanceof _ApiError ? err.message : 'That did not work.')
    } finally { setBusy(false) }
  }

  return (
    <div className="login-page">
      <aside className="login-art">
        <div className="row" style={{ gap: 10 }}>
          <span className="rail-mark"><IconLeaf size={16} color="#fff" /></span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Ledger</span>
        </div>
        <div>
          <h2>{heroTitle}</h2>
          <p>{heroText}</p>
        </div>
        <div className="login-facts">
          {facts.map(([big, small]) => (
            <div className="login-fact" key={small}><b>{big}</b><span>{small}</span></div>
          ))}
        </div>
      </aside>

      <div className="login-form-wrap">
        <form className="login-form" onSubmit={step === 'phone' ? sendCode : verify}>
          <button
            type="button"
            className="btn quiet"
            style={{ alignSelf: 'flex-start', minHeight: 30 }}
            onClick={() => (step === 'code' ? setStep('phone') : onBack())}
          >
            <IconChevron size={14} dir="left" /> {step === 'code' ? 'Change number' : 'Not you?'}
          </button>

          <div>
            <h1>{title}</h1>
            <p className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
              {step === 'phone'
                ? 'No password. We send a code to your phone.'
                : `We sent a code to ${maskedPhone(msisdn)}`}
            </p>
          </div>

          {step === 'phone' ? (
            <div className="field">
              <label htmlFor="phone">Your phone number</label>
              <input
                id="phone"
                className="input num"
                style={{ fontSize: 'var(--fs-xl)', minHeight: 56, textAlign: 'center', letterSpacing: '.04em' }}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="0712 345 678"
                value={msisdn}
                onChange={(e) => setMsisdn(e.target.value)}
                required
                autoFocus
              />
              <span className="hint">{hint}</span>
            </div>
          ) : (
            <div className="field">
              <label htmlFor="code">Six-digit code</label>
              <input
                id="code"
                className="input num"
                style={{ fontSize: 'var(--fs-3xl)', minHeight: 64, textAlign: 'center', letterSpacing: '.35em' }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
              <span className="hint">It expires in ten minutes.</span>
            </div>
          )}

          {devCode && (
            <Banner tone="info" title={`Your code is ${devCode}`}>
              Shown because no SMS gateway is connected yet. On a live system this arrives by text.
            </Banner>
          )}

          {error && <div className="error-text">{error}</div>}

          <button
            className="btn lg block"
            disabled={busy || (step === 'phone' ? msisdn.trim().length < 9 : code.length < 6)}
          >
            {busy ? 'Please wait…' : step === 'phone' ? 'Send me a code' : 'Sign in'}
          </button>

          <p className="hint" style={{ textAlign: 'center' }}>{footer}</p>
        </form>
      </div>
    </div>
  )
}
